/* 武將定義的共用部分：陣營配色、兵器分類、defineHero、瞄準方式。
 * 每名武將一個檔案（heroes/<陣營>/<id>.ts），用 defineHero 宣告；名冊順序在 heroes/index.ts。 */
import { SG } from '../core/sg.ts';
import { on } from '../core/hooks.ts';
import type { HeroDefinition, HookSite, HookSigs, HookEntry, HeroSpec, Look, SkillTuple, Weapon } from './types.ts';

const PAL: Record<string, Record<string, string | null>> = {
  shu: { armor: '#3e7b50', sleeve: '#2f5e3f', cloth: '#2c573b', trim: '#d9b45c', pants: '#2a332c', boot: '#2b2420', cape: null, belt: '#6b3b24', metal: '#b9c1c8', hood: '#46935c', pole: '#5a3a22', plume: '#c23b2e' },
  wu: { armor: '#a93a30', sleeve: '#7f2a24', cloth: '#b33a30', trim: '#e9c46a', pants: '#3a2323', boot: '#2a1f1f', cape: null, belt: '#6e1f1b', metal: '#c9ced4', hood: '#b33a30', pole: '#5a3a22', plume: '#e9c46a' },
  wei: { armor: '#3b5a99', sleeve: '#2b3f6e', cloth: '#26335f', trim: '#c9a24e', pants: '#1f2740', boot: '#262a36', cape: null, belt: '#1a2238', metal: '#8e97a8', hood: '#26335f', pole: '#3b4058', plume: '#4f86e0' },
};
const WCLASS: Record<string, string> = { guandao: 'pole', halberd: 'pole', axe: 'pole', spear: 'spear', snake: 'spear', sword: 'sword', twin: 'twin', bow: 'bow', fan: 'fan', staff: 'staff' };
const WNAME: Record<string, string> = { guandao: '大刀', halberd: '戟', axe: '大斧', spear: '槍', snake: '蛇矛', sword: '劍', twin: '雙戟', bow: '弓', fan: '羽扇', staff: '杖' };
SG.KEYS = ['防 → 攻', '防 ↑ 攻', '防 ↓ 攻', '防 → 跳'];
SG.SLOT = ['Q', 'W', 'E', 'R'];

// 展開成模擬與畫面用的武將設定（faction 也可以是 'neutral'：中立首領）
export function H(id: string, name: string, sub: string, faction: string, role: string, weapon: Weapon, look: Look, skills: SkillTuple[]): HeroSpec {
  const c = Object.assign({}, PAL[faction], look.c || {});
  const h = Object.assign({ skin: '#f0caa0', hair: '#1c1a1c', eye: 'round', beard: null, head: 'helmet' }, look, {
    id, name, sub, faction, role, weapon, c,
    wclass: look.wclass || (look.dual ? 'twin' : WCLASS[weapon]), // look.wclass：架式另外指定（凌統三節棍用長兵器的架式）
    weaponName: look.weaponName || (look.dual ? '雙' + WNAME[weapon] : WNAME[weapon]),
    ranged: look.ranged != null ? look.ranged : ['bow', 'fan', 'staff'].includes(weapon) || role === '射手',
  }) as unknown as HeroSpec; // skills、skill 在下面補上
  h.skills = skills.map((s, i: number) => mkSkill(s, i));
  const r = h.skills[3];
  h.skill = { name: r.name, keys: r.keys, desc: r.desc, style: r.arch, hitAt: SG.HIT_AT[r.arch] || 0.5 };
  return h;
}

// 一招的資料。then：兩段式技能的第二段（另一招，例如李星 Q1 → Q2），格式跟一般技能一樣，放在同一格；
// 第二段沒寫 mp 就不耗資源。thenOn: 'hit' 表示第一段打中才開第二段（技能程式呼叫 W.armThen），沒寫就是放了就開
function mkSkill(s: any, i: number): any {
  const o: any = Object.assign({ slot: SG.SLOT[i], keys: SG.KEYS[i], name: s[0], arch: s[1], desc: s[2], passive: s[1] === 'passive' }, s[3] || {});
  if (o.wardHop) o.desc += '也可以點自己的眼（斥候旗）直接瞬移過去。'; // 跳眼（v0.5.0）
  if (o.then) { const t = o.then; o.then = Object.assign({ slot: o.slot, keys: o.keys, name: t[0], arch: t[1], desc: t[2], passive: false, mp: 0, stage2: true }, t[3] || {}); }
  return o;
}

// 3.7D 技能的瞄準方式：U 指定單體（點在目標附近）、P 指定地點、S 以自己為中心、D 朝方向
export const U = (range: number | number[]) => ({ kind: 'unit', range, area: 60 }); // 指定單體：要點在一個單位上
// line：[半長, 寬] 的話，落點畫成垂直於施放方向的長條（孫權 長江天塹），不畫圓。
// face: 'home' 改成背對自家本營、往前 off 的位置（郝昭 陳倉城壘的城垛）
export const P = (range: number | number[], area: number | number[], line?: [number, number], face?: { face: 'home'; off: number }) => ({ kind: 'point', range, area, line, ...face });
export const S = (area: number | number[]) => ({ kind: 'self', area });
export const D = (range: number | number[], width: number | number[]) => ({ kind: 'dir', range, width });

// 天賦被動（不佔技能格）：說明裡的 {a/b/c} 依等級段落 passive.lv（預設只有一段）取值
SG.talTier = (h: any, lv: number) => { const p = h && h.passive; if (!p || !p.lv) return 0; let k = 0; p.lv.forEach((x: number, j: number) => { if (lv >= x) k = j; }); return k; };
SG.talDesc = (h: any, lv: number, fmt?: (v: string, g: string) => string) => h.passive.desc.replace(/\{([^}]+)\}/g, (_: string, g: string) => { const a = g.split('/'), v = a[Math.min(a.length - 1, SG.talTier(h, lv))]; return fmt ? fmt(v, g) : v; });

// 一名武將的定義。role 可以寫「主/副」（例如 '戰士/刺客'）：主定位決定成長模板與推薦出裝，副定位用於顯示與篩選。
// skills：四招 [名稱, 原型, 說明, 參數]，說明中的 {a/b/c/d} 是各等級的數值（大招 3 級）。
//
// 除了資料，武將檔也放這名武將專屬的程式：
//   kinds    技能種類 x.k 的實作（掛成 World 的 x_<種類>），this 是 World
//   methods  只有這名武將用到的 World 方法
//   hooks    被動與專屬效果：hook(時間點, 順位, function (...) { })，時間點一覽見 core/hooks.ts
//   ai       電腦什麼時候放這名武將的技能：{ 種類: (c) => boolean }，c = { w, u, t, d, hero, hpR, foeHeroes }
//   aiSig    同上，但以被動標記 sig 判斷（招式沒有專屬種類時）
//   sigSkills 技能資料標了 sig 的招式：取代該原型的一般動作，(u, a, k, dt, cross)
// 一個掛勾：在哪個時間點、第幾順位（同一時間點內小的先做）、做什麼。處理函式的參數依時間點帶入型別
export function hook<S extends HookSite>(site: S, order: number, fn: HookSigs[S]): HookEntry {
  return [site, order, fn] as HookEntry;
}
export function defineHero(d: HeroDefinition) {
  const [role, role2] = d.role.split('/');
  const h = H(d.id, d.name, d.sub, d.faction, role, d.weapon, d.look, d.skills);
  h.role2 = role2 || null;
  h.roleText = h.role + (h.role2 ? '/' + h.role2 : '');
  if (d.res) h.res = d.res;
  if (d.skillOrder) h.skillOrder = d.skillOrder; // 電腦自動配點的順序（core/systems.ts SG.skillLevels）
  if (d.res === 'none') for (const s of h.skills) { s.mp = 0; if (s.then) s.then.mp = 0; } // 不耗資源：每一招都不耗
  if (d.passive) { const [name, desc, o] = d.passive; h.passive = Object.assign({ name, desc }, o || {}); } // 天賦被動（不佔技能格）
  if (d.forms) buildForms(h, d);
  (SG.HERO_MODS || (SG.HERO_MODS = [])).push({ id: d.id, kinds: d.kinds || {}, methods: d.methods || {}, ai: d.ai || {}, aiSig: d.aiSig || {}, sigSkills: d.sigSkills || {} });
  for (const [site, order, fn] of d.hooks || []) on(site, order, d.id, fn);
  return h;
}

// 形態（v0.5.0）：每個形態一份設定，以本體為原型（Object.create），只蓋掉技能、遠近程、兵器；
// 切換時把 u.spec 換成那個形態的設定，讀 u.spec.skills／ranged／兵器的地方自然就跟著變。R 共用同一個物件。
function buildForms(h: HeroSpec, d: HeroDefinition) {
  const forms = d.forms!.map((f, k) => {
    if (k === 0) return { id: f.id, name: f.name, ranged: f.ranged, spec: h };
    const look = Object.assign({}, d.look, f.look || {}), weapon = f.weapon || d.weapon;
    const s = Object.create(h) as HeroSpec;
    s.ranged = f.ranged;
    s.weapon = weapon;
    s.dual = !!look.dual;
    s.wclass = look.dual ? 'twin' : WCLASS[weapon];
    s.weaponName = look.weaponName || (look.dual ? '雙' + WNAME[weapon] : WNAME[weapon]);
    s.skills = (f.skills || []).slice(0, 3).map((t, i) => mkSkill(t, i)).concat([h.skills[3]]);
    return { id: f.id, name: f.name, ranged: f.ranged, spec: s };
  });
  h.forms = forms;
  h.ranged = d.forms![0].ranged;
  if (d.formAi) h.formAi = d.formAi;
}

// 介紹頁、選角列技能用：一般武將就是 Q/W/E/R；有形態的武將每個形態的 Q/W/E 都列出來，標上形態名稱，最後是 R
SG.skillRows = (h: HeroSpec) => {
  if (!h.forms) return h.skills.map((s: any, i: number) => ({ i, s, tag: '' }));
  const rows: { i: number; s: any; tag: string }[] = [];
  for (const f of h.forms) f.spec.skills.slice(0, 3).forEach((s: any, i: number) => rows.push({ i, s, tag: f.name }));
  rows.push({ i: 3, s: h.skills[3], tag: '' });
  return rows;
};
// 二段式技能的第二段（練習選將、角色頁、武將譜共用）：介面上原本只看得到第一段。fmt 處理說明裡的 {a/b/c} 數字
SG.stageNote = (s: any, fmt: (d: string) => string) => {
  if (!s || !s.then) return '';
  return `<b>第二段「${s.then.name}」</b>${s.thenOn === 'hit' ? '（第一段打中才能放）' : ''}：${fmt(s.then.desc)}`;
};
// 名詞說明：技能或天賦提到「水裡」「濕」的武將（甘寧、孫權、凌統、荀攸…），在技能列表後面補一段
// 規則見 core/elements.ts：SG.inWaterArea（在水裡）、W.isWet（濕）
const TERMS = [
  { re: /水裡|水域|淺灘|江水|濕/, name: '在水裡、濕', desc: '站在江面上（浮橋不算）、淺灘、技能鋪出的水域（孫權、凌統）或荀攸「決水灌城」的範圍裡，就算在水裡，也是濕的。另外，冰系技能留在地上的範圍也算在水裡；身上有冰印記也算濕。濕的人被雷打會導電（跳到附近濕的敵人）、被冰打會凍結 0.9 秒、被火打會蒸發（傷害只剩 80%）。' },
];
SG.heroTerms = (h: HeroSpec) => {
  const sks = (h.forms ? h.forms.flatMap((f: any) => f.spec.skills) : h.skills).flatMap((s: any) => (s.then ? [s, s.then] : [s]));
  const text = sks.map((s: any) => s.desc).join(' ') + ' ' + (h.passive ? h.passive.desc : '');
  return TERMS.filter((t) => t.re.test(text));
};
// 遠戰／近戰、兵器名稱：有形態的武將兩個都寫
SG.rangeText = (h: HeroSpec) => (h.forms ? h.forms.map((f: any) => (f.ranged ? '遠戰' : '近戰')).join('／') : h.ranged ? '遠戰' : '近戰');
SG.weaponText = (h: HeroSpec) => (h.forms ? h.forms.map((f: any) => f.spec.weaponName).join('／') : h.weaponName);
